/**
 * The mechanics gate (roadmap-3k §8.1; launch plan, Phase 1 decisions). The ten roadmap mechanics
 * all ship on rules v2, so a pack's `minRulesVersion` cannot tell a client which of them its
 * levels need. A city-pack manifest therefore also lists the mechanics its levels use
 * (tools/src/art/citypack.ts, carried by GET /v1/content/packs), and a build whose engine lacks
 * one of them shows "update the app" instead of playing it wrong (apps/game `needsAppUpdate`).
 * The API refuses to publish such a pack as well: its replay could not verify the rounds.
 */
import type { Mechanic } from './types.js';

/**
 * What this engine build plays, keyed by `Mechanic` so the two cannot drift: an id added to that
 * type does not compile until it is listed here too, which is the moment the engine plays it.
 */
const IMPLEMENTED: Record<Mechanic, true> = {
  clutter: true,
  wrapped: true,
  twoCustomers: true,
  rush: true,
  crates: true,
  ice: true,
  conveyor: true,
  glue: true,
  bagLock: true,
  dispenser: true,
  lockKey: true,
  clearGoal: true,
  hiddenLine: true,
  collectGoal: true,
  tray: true,
  threeCustomers: true,
  shelf: true,
};

/** Every mechanic this engine build implements, in the tools' canonical order (level-specs `MECHANICS`). */
export const SUPPORTED_MECHANICS: readonly Mechanic[] = Object.freeze(Object.keys(IMPLEMENTED) as Mechanic[]);

const SUPPORTED = new Set<string>(SUPPORTED_MECHANICS);

/** The ids in `mechanics` this engine does not implement (unknown ids included), in list order. */
export function unsupportedMechanics(mechanics: readonly string[]): string[] {
  return mechanics.filter((m) => !SUPPORTED.has(m));
}
